import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/profile-components/Navbar";
import Topbar from "../components/profile-components/Topbar";
import AddressVerificationFields from "../components/common/AddressVerificationFields";
import API_BASE_URL from "../config";
import "./SalesPages.css";

function buildSubdomainFromBusinessName(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function NewSubscriber() {
  const navigate = useNavigate();

  const [plans, setPlans] = useState([]);
  const [saving, setSaving] = useState(false);
  const [checkingSubdomain, setCheckingSubdomain] = useState(false);
  const [subdomainStatus, setSubdomainStatus] = useState(null);
  const [subdomainManuallyEdited, setSubdomainManuallyEdited] = useState(false);

  const [businessName, setBusinessName] = useState("");
  const [primaryContactName, setPrimaryContactName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [npn, setNpn] = useState("");
  const [status, setStatus] = useState("pending");
  const [source, setSource] = useState("");
  const [planName, setPlanName] = useState("");
  const [subscriptionPlanId, setSubscriptionPlanId] = useState("");
  const [subscriptionStatus, setSubscriptionStatus] = useState("active");
  const [billingStatus, setBillingStatus] = useState("trial");
  const [billingFrequency, setBillingFrequency] = useState("monthly");
  const [monthlyAmount, setMonthlyAmount] = useState("");
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  });

  const [subdomain, setSubdomain] = useState("");
  const [billingContactName, setBillingContactName] = useState("");
  const [billingContactEmail, setBillingContactEmail] = useState("");

  useEffect(() => {
    loadPlans();
  }, []);

  const selectedPlan = useMemo(
    () => plans.find((plan) => String(plan.id) === String(subscriptionPlanId)),
    [plans, subscriptionPlanId]
  );

  const suggestedSubdomain = useMemo(() => {
    return buildSubdomainFromBusinessName(businessName);
  }, [businessName]);

  const handleBusinessNameChange = (value) => {
    setBusinessName(value);
    setSubdomainStatus(null);

    if (!subdomainManuallyEdited) {
      setSubdomain(buildSubdomainFromBusinessName(value));
    }
  };

  const handleSubdomainChange = (value) => {
    const cleaned = buildSubdomainFromBusinessName(value);

    setSubdomain(cleaned);
    setSubdomainStatus(null);

    if (!cleaned) {
      setSubdomainManuallyEdited(false);
      return;
    }

    setSubdomainManuallyEdited(cleaned !== suggestedSubdomain);
  };

  useEffect(() => {
    if (!billingContactName && primaryContactName) {
      setBillingContactName(primaryContactName);
    }
  }, [primaryContactName, billingContactName]);

  useEffect(() => {
    if (!billingContactEmail && email) {
      setBillingContactEmail(email);
    }
  }, [email, billingContactEmail]);

  const computedDates = useMemo(() => {
    if (!startDate) {
      return {
        trialStartDate: "",
        trialEndDate: "",
        firstPaymentDate: "",
        billingDayLabel: ""
      };
    }

    const start = new Date(`${startDate}T00:00:00`);

    if (Number.isNaN(start.getTime())) {
      return {
        trialStartDate: "",
        trialEndDate: "",
        firstPaymentDate: "",
        billingDayLabel: ""
      };
    }

    const trialEnd = new Date(start);
    trialEnd.setDate(trialEnd.getDate() + 29);

    const firstPayment = new Date(start);
    firstPayment.setDate(firstPayment.getDate() + 30);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    return {
      trialStartDate: formatDate(start),
      trialEndDate: formatDate(trialEnd),
      firstPaymentDate: formatDate(firstPayment),
      billingDayLabel: `${firstPayment.getDate()}${getOrdinalSuffix(firstPayment.getDate())} of each month`
    };
  }, [startDate]);

  function getOrdinalSuffix(day) {
    if (day >= 11 && day <= 13) return "th";

    const lastDigit = day % 10;

    if (lastDigit === 1) return "st";
    if (lastDigit === 2) return "nd";
    if (lastDigit === 3) return "rd";

    return "th";
  }

  const loadPlans = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await fetch(`${API_BASE_URL}/api/subscription-plans`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!res.ok) return;

      const data = await res.json();
      setPlans(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Load plans error:", err.message);
    }
  };

  const addressValues = {
    street_address: streetAddress,
    city,
    state,
    zip_code: zipCode
  };

  const handleAddressChange = (fieldName, value) => {
    if (fieldName === "street_address") setStreetAddress(value);
    if (fieldName === "city") setCity(value);
    if (fieldName === "state") setState(value);
    if (fieldName === "zip_code") setZipCode(value);
  };

  const checkSubdomain = async (value = subdomain) => {
    try {
      const trimmed = String(value || "").trim();

      if (!trimmed) {
        setSubdomainStatus(null);
        return null;
      }

      setCheckingSubdomain(true);

      const token = localStorage.getItem("token");
      const res = await fetch(
        `${API_BASE_URL}/api/subscribers/check-subdomain?value=${encodeURIComponent(trimmed)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to check subdomain");
      }

      setSubdomainStatus(data);
      return data;
    } catch (err) {
      console.error("Check subdomain error:", err.message);

      const failed = {
        available: false,
        reason: err.message || "Failed to check subdomain"
      };

      setSubdomainStatus(failed);
      return failed;
    } finally {
      setCheckingSubdomain(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const token = localStorage.getItem("token");

      if (!businessName.trim()) {
        throw new Error("Business name is required");
      }

      if (!primaryContactName.trim()) {
        throw new Error("Primary contact name is required");
      }

      if (!email.trim()) {
        throw new Error("Primary contact email is required");
      }

      if (!subscriptionPlanId) {
        throw new Error("Plan is required");
      }

      if (!subdomain.trim()) {
        throw new Error("Subdomain is required");
      }

      if (!startDate) {
        throw new Error("Start date is required");
      }

      const cleanedSubdomain = buildSubdomainFromBusinessName(subdomain);

      if (!cleanedSubdomain) {
        throw new Error("A valid subdomain is required");
      }

      const subdomainResult = await checkSubdomain(cleanedSubdomain);

      if (!subdomainResult?.available) {
        throw new Error(subdomainResult?.reason || "That subdomain is not available");
      }

      const res = await fetch(`${API_BASE_URL}/api/subscribers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          business_name: businessName,
          primary_contact_name: primaryContactName,
          email,
          phone,
          street_address: streetAddress,
          city,
          state,
          zip_code: zipCode,
          npn,
          status,
          source: source || "manual_platform_create",
          subscription_plan_id: subscriptionPlanId,
          plan_name: planName,
          subscription_status: subscriptionStatus,
          billing_status: billingStatus,
          billing_frequency: billingFrequency || null,
          monthly_amount: monthlyAmount || null,
          start_date: startDate,
          subdomain: cleanedSubdomain,
          billing_contact_name: billingContactName || primaryContactName,
          billing_contact_email: billingContactEmail || email,
          trial_starts_at: computedDates.trialStartDate,
          trial_ends_at: computedDates.trialEndDate
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to create subscriber");
      }

      if (data.activation_url) {
        alert(`Subscriber created.\n\nActivation URL:\n${data.activation_url}`);
      }

      navigate(`/subscriber-profile/${data.id}`);
    } catch (err) {
      console.error("Create subscriber error:", err.message);
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page">
        <div className="sales-hero">
          <div>
            <p className="sales-eyebrow">Agent Book CRM</p>
            <h1 className="sales-title">New Subscriber</h1>
            <p className="sales-subtitle">
              Create a subscriber account, assign the subdomain, and generate the owner onboarding link.
            </p>
          </div>

          <div className="sales-header-actions">
            <button className="sales-secondary-btn" onClick={() => navigate("/subscribers")}>
              Cancel
            </button>
            <button className="sales-primary-btn" onClick={handleSave} disabled={saving || checkingSubdomain}>
              {saving ? "Creating..." : "Create Subscriber"}
            </button>
          </div>
        </div>

        <div className="sales-toolbar-card">
          <div className="sales-form-fields">
            <div className="sales-field">
              <label>New Subscriber Model</label>
              <input value="Active subscriber with 30-day trial billing period" readOnly />
            </div>
          </div>
        </div>

        <div className="sales-form-grid">
          <div className="sales-section-card sales-section-lead">
            <div className="sales-section-head">
              <h2>Subscriber Information</h2>
              <p>Core business, owner, and subdomain setup.</p>
            </div>

            <div className="sales-form-fields">
              <div className="sales-field sales-field-full">
                <label>Business Name *</label>
                <input value={businessName} onChange={(e) => handleBusinessNameChange(e.target.value)} />
              </div>

              <div className="sales-field">
                <label>Primary Contact Name *</label>
                <input value={primaryContactName} onChange={(e) => setPrimaryContactName(e.target.value)} />
              </div>

              <div className="sales-field">
                <label>Primary Contact Email *</label>
                <input value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>

              <div className="sales-field">
                <label>Phone</label>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>

              <div className="sales-field">
                <label>NPN</label>
                <input value={npn} onChange={(e) => setNpn(e.target.value)} />
              </div>

              <AddressVerificationFields
                values={addressValues}
                onChange={handleAddressChange}
                streetLabel="Street Address"
                showUnit={false}
                showCounty={false}
                fieldMap={{
                  street: "street_address",
                  city: "city",
                  state: "state",
                  zip: "zip_code",
                  county: "county",
                  addressVerified: "address_verified"
                }}
              />

              <div className="sales-field">
                <label>Status</label>
                <select value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="pending">Pending</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="sales-field">
                <label>Source</label>
                <input value={source} onChange={(e) => setSource(e.target.value)} placeholder="manual_platform_create" />
              </div>

              <div className="sales-field">
                <label>Subdomain *</label>
                <input
                  value={subdomain}
                  onChange={(e) => handleSubdomainChange(e.target.value)}
                  onBlur={() => checkSubdomain()}
                  placeholder={suggestedSubdomain || "your-subscriber-name"}
                />
                {checkingSubdomain && <small>Checking subdomain...</small>}
                {!checkingSubdomain && subdomainStatus?.available && (
                  <small style={{ color: "#15803d" }}>Subdomain is available.</small>
                )}
                {!checkingSubdomain && subdomainStatus && !subdomainStatus.available && (
                  <small style={{ color: "#b91c1c" }}>{subdomainStatus.reason || "Subdomain is unavailable."}</small>
                )}
              </div>

              <div className="sales-field">
                <label>Full Domain</label>
                <input
                  value={subdomain ? `${subdomain.trim().toLowerCase()}.agentbookcrm.com` : ""}
                  readOnly
                />
              </div>
            </div>
          </div>

          <div className="sales-section-card sales-section-subscription">
            <div className="sales-section-head">
              <h2>Subscription Information</h2>
              <p>Plan, billing, onboarding setup, and automatic trial billing schedule.</p>
            </div>

            <div className="sales-form-fields">
              <div className="sales-field">
                <label>Plan *</label>
                <select
                  value={subscriptionPlanId}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    setSubscriptionPlanId(selectedId);

                    const plan = plans.find((item) => String(item.id) === String(selectedId));
                    setPlanName(plan?.plan_key || "");

                    if (plan?.monthly_price != null && !monthlyAmount) {
                      setMonthlyAmount(String(plan.monthly_price));
                    }
                  }}
                >
                  <option value="">Select Plan</option>
                  {plans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.plan_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sales-field">
                <label>Subscription Status</label>
                <select value={subscriptionStatus} onChange={(e) => setSubscriptionStatus(e.target.value)}>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div className="sales-field">
                <label>Billing Status</label>
                <select value={billingStatus} onChange={(e) => setBillingStatus(e.target.value)}>
                  <option value="trial">Trial</option>
                  <option value="current">Current</option>
                  <option value="past_due">Past Due</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div className="sales-field">
                <label>Billing Frequency</label>
                <select value={billingFrequency} onChange={(e) => setBillingFrequency(e.target.value)}>
                  <option value="">Select</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annually">Annually</option>
                </select>
              </div>

              <div className="sales-field">
                <label>Monthly Amount</label>
                <input value={monthlyAmount} onChange={(e) => setMonthlyAmount(e.target.value)} />
              </div>

              <div className="sales-field">
                <label>Start Date *</label>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>

              <div className="sales-field">
                <label>Trial End Date</label>
                <input type="date" value={computedDates.trialEndDate} readOnly />
              </div>

              <div className="sales-field">
                <label>First Payment Date</label>
                <input type="date" value={computedDates.firstPaymentDate} readOnly />
              </div>

              <div className="sales-field">
                <label>Recurring Billing Day</label>
                <input value={computedDates.billingDayLabel} readOnly />
              </div>

              <div className="sales-field">
                <label>Billing Contact Name</label>
                <input value={billingContactName} onChange={(e) => setBillingContactName(e.target.value)} />
              </div>

              <div className="sales-field">
                <label>Billing Contact Email</label>
                <input value={billingContactEmail} onChange={(e) => setBillingContactEmail(e.target.value)} />
              </div>

              <div className="sales-field sales-field-full">
                <label>Billing Model</label>
                <input value="30-day free/promo billing period, first payment on day 31" readOnly />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default NewSubscriber;